<template>
    <div>
        <template v-if="isModal">
            <modal id="orderTicketsModal" v-if="showModal" @close="onClose" ref="modalRef">
                <template #header>
                    Add {{ form.symbol }} to Order Tickets
                </template>

                <template #body>
                    <div id="orderTicketsFormPortal" class="py-2"></div>
                </template>

                <template #footer v-if="!errorSymbolQuotes">
                    <div class="alert alert-danger mt-4 mb-6 text-sm" v-if="hasErrors()">
                        <p v-for="error in errors" :key="error">&bull; {{ error[0] }}</p>
                    </div>
                    <div class="flex justify-center">
                        <div class="btn btn-sm btn-yellow-ghost inline-flex mx-1" @click="onClose">Cancel</div>
                        <div class="btn btn-sm btn-yellow inline-flex mx-1" @click="onAdd">Add Ticket</div>
                    </div>
                </template>
            </modal>
        </template>
        <template v-else>
            <div id="orderTicketsFormPortal"></div>
        </template>

        <flash-message :secondsToDisplay="5" ref="flash"></flash-message>

        <teleport to="#orderTicketsFormPortal" v-if="renderTeleportedContent">
            <div id="orderTicketsForm" class="relative mt-4">
                <div v-if="!loading.section" class="w-full">
                    <template v-if="isAdd">
                        <div class="border-t-2 border-blue-600">
                            <div class="w-full bg-tan-200 px-4 py-2 border mb-4 border-gray-200 border-t-0">
                                <div class="font-bold mb-1">SELECT:</div>
                                <div v-if="section === 'future'" class="flex flex-wrap">
                                    <contract-selector
                                        v-model="form.symbol"
                                        :initial="sectionQuotes.symbol"
                                        :all-contracts="sectionQuotes.futures">
                                    </contract-selector>
                                </div>

                                <div v-if="section === 'future-options'">
                                    <option-selector-2
                                        v-model="form.symbol"
                                        :initial-symbol="sectionQuotes.underlying"
                                        :groups="sectionQuotes.options || sectionQuotes"
                                        >
                                    </option-selector-2>
                                </div>

                                <div v-if="section === 'forex'">
                                    <forex-selector
                                        v-model="form.symbol"
                                        default-symbol="^AUDJPY"
                                        :symbols="sectionQuotes.forex">
                                    </forex-selector>
                                </div>
                            </div>
                        </div>
                    </template>

                    <div class="w-full text-center" v-show="!loading.section && form.symbol">
                        <spinner v-if="loading.symbolQuotes"></spinner>
                    </div>

                    <template v-if="!loading.symbolQuotes && symbolQuotes && symbolQuotes.symbolName">
                        <div class="w-full border border-gray-400 rounded-lg mt-4">
                            <div class="px-3 md:px-10 py-2">
                                <div class="subsection pb-4 text-center">
                                    <p class="text-xl leading-none mb-2 font-bold text-blue-500">{{ form.symbol }} - {{ symbolQuotes.contractFullName || symbolQuotes.symbolName }}</p>
                                    <p class="text-base leading-none"><span class="font-bold">Last Price:</span> {{ symbolQuotes.lastPrice }}</p>
                                </div>

                                <div class="w-full mb-6">
                                    <div class="flex flex-wrap -mx-3 lg:-mx-6 mt-4">
                                        <div class="w-full md:w-1/2 px-3 lg:px-6">
                                            <div class="flex flex-wrap">
                                                <div class="w-full md:w-1/2">
                                                    <label class="form-label-class mt-2 md:mt-1" for="ticket-number">
                                                        Ticket Number<sup>*</sup>
                                                        <span @click="generateTicketNumber" v-tooltip="{ content: 'Generate the random Ticket Number', placement: 'auto-start', theme: 'info' }" class="float-right mr-2 inline-block cursor-pointer text-yellow-500 has-tooltip text-sm">
                                                            <i class="fas fa-magic"></i>
                                                        </span>
                                                    </label>
                                                </div>
                                                <div class="w-full md:w-1/2">
                                                    <input class="form-input form-input-sm" :class="{ 'is-invalid' : errors.ticket_number }" id="ticket-number" type="text" placeholder="e.g. SYM-YYHHMM-#" v-model="form.ticket_number">
                                                </div>
                                                <div class="w-full md:w-1/2">
                                                    <label class="form-label-class mt-2 md:mt-1" for="order-type">Order Type<sup>*</sup></label>
                                                </div>
                                                <div class="w-full md:w-1/2">
                                                    <select class="form-control form-control-sm form-control-no-shadow" :class="{ 'is-invalid' : errors.order_type }" id="order-type" v-model="form.order_type">
                                                        <option value="">Select One</option>
                                                        <option value="D" :selected="form.order_type == 'D'">Day Order</option>
                                                        <option value="C" :selected="form.order_type == 'C'">Good Until Cancelled Order</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="w-full md:w-1/2 px-3 lg:px-6">
                                            <div class="flex flex-wrap">
                                                <div class="w-full md:w-1/2">
                                                    <label class="form-label-class mt-2 md:mt-1" for="trend">Trend Seeker Trend</label>
                                                </div>
                                                <div class="w-full md:w-1/2">
                                                    <select @change="form.trend_signal=''" class="form-control form-control-sm form-control-no-shadow" :class="{ 'is-invalid' : errors.trend_seeker }" id="trend" v-model="form.trend_seeker">
                                                        <option value="">Select One</option>
                                                        <option value="B" :selected="form.trend_seeker == 'B'">Up</option>
                                                        <option value="H" :selected="form.trend_seeker == 'H'">Neutral</option>
                                                        <option value="S" :selected="form.trend_seeker == 'S'">Down</option>
                                                    </select>
                                                </div>
                                                <div class="w-full md:w-1/2">
                                                    <label class="form-label-class mt-2 md:mt-1" for="signal-ranking">Trend Signal Ranking</label>
                                                </div>
                                                <div class="w-full md:w-1/2">
                                                    <select class="form-control form-control-sm form-control-no-shadow" :class="{ 'is-invalid' : errors.trend_signal }" id="signal-ranking" v-model="form.trend_signal">
                                                    <!-- <select class="form-control form-control-sm form-control-no-shadow" :class="{ 'is-invalid' : errors.trend_signal }" id="signal-ranking" v-model.text="form.trend_signal"> -->
                                                        <template v-if='form.trend_seeker == "H"'>
                                                        <option value="">Select One</option>
                                                        <option value="5" :selected="form.trend_signal == '5'">Bullish</option>
                                                        <option value="3" :selected="form.trend_signal != '1' && form.trend_signal != '5'">Neutral</option>
                                                        <option value="1" :selected="form.trend_signal == '1'">Bearish</option>
                                                        </template>
                                                        <template v-else>
                                                        <option value="">Select One</option>
                                                        <option value="5" :selected="form.trend_signal == '5'">Extreme</option>
                                                        <option value="4" :selected="form.trend_signal == '4'">Strong</option>
                                                        <option value="3" :selected="form.trend_signal == '3'">Moderate</option>
                                                        <option value="2" :selected="form.trend_signal == '2'">Neutral</option>
                                                        <option value="1" :selected="form.trend_signal == '1'">Weakest</option>
                                                        </template>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="subsection pb-1">
                                    <span class="text-lg font-bold mr-1">Trade Information</span>
                                    <div v-tooltip="{ content: 'Prices must be entered according to «Last Price» format', placement: 'auto-start', theme: 'info' }" class="inline-block text-blue-500 has-tooltip text-sm"><i class="fas fa-question-circle"></i></div>
                                </div>
                                <div class="w-full mb-6">
                                    <div class="flex flex-wrap -mx-3 lg:-mx-6 mt-4">
                                        <div class="w-full md:w-1/2 px-3 lg:px-6">
                                            <div class="flex flex-wrap">
                                                <div class="w-full md:w-1/2">
                                                    <label class="form-label-class mt-2 md:mt-1" for="long-short">Trade Type<sup>*</sup></label>
                                                </div>
                                                <div class="w-full md:w-1/2">
                                                    <select class="form-control form-control-sm form-control-no-shadow" :class="{ 'is-invalid' : errors.long_short }" id="long-short" v-model="form.long_short">
                                                        <option value="L" :selected="form.long_short == 'L'">Long</option>
                                                        <option value="S" :selected="form.long_short == 'S'">Short</option>
                                                    </select>
                                                </div>
                                                <div class="w-full md:w-1/2">
                                                    <label class="form-label-class mt-2 md:mt-1" for="entry-price">
                                                        <template v-if="section == 'future-options'">Entry Premium<sup>*</sup></template>
                                                        <template v-else>Entry Price<sup>*</sup></template>
                                                    </label>
                                                </div>
                                                <div class="w-full md:w-1/2">
                                                    <price-input :symbol="form.symbol" v-model="form.entry_price" :base-code="symbolQuotes.baseCode" :default-value="form.entry_price" :class="{'is-invalid': errors.entry_price}" id="entry-price" class="form-input form-input-sm form-input-modal"></price-input>
                                                </div>
                                                <div class="w-full md:w-1/2">
                                                    <label class="form-label-class mt-2 md:mt-1" for="date-filled">Date Filled</label>
                                                </div>
                                                <div class="w-full md:w-1/2">
                                                    <input class="form-input form-input-sm" :class="{ 'is-invalid' : errors.entry_date }" id="date-filled" type="date" pattern="[0-9]{2}/[0-9]{2}/[0-9]{4}" v-model="form.entry_date">
                                                </div>
                                                <div class="w-full md:w-1/2">
                                                    <label class="form-label-class mt-2 md:mt-1" for="nbr-of-contracts">
                                                        <template v-if="section == 'future-options'"># of Options<sup>*</sup></template>
                                                        <template v-else># of Contracts<sup>*</sup></template>
                                                    </label>
                                                </div>
                                                <div class="w-full md:w-1/2">
                                                    <input class="form-input form-input-sm" :class="{ 'is-invalid' : errors.quantity }" id="nbr-of-contracts" type="number" placeholder="0" v-model="form.quantity" min="0">
                                                </div>

                                                <template v-if="section != 'future-options'">
                                                <div class="w-full md:w-1/2">
                                                    <label class="form-label-class mt-2 md:mt-1" for="stop-loss">Stop Loss</label>
                                                </div>
                                                <div class="w-full md:w-1/2">
                                                    <price-input :symbol="form.symbol" v-model="form.stop_loss" :base-code="symbolQuotes.baseCode" :default-value="form.stop_loss" :class="{'is-invalid': errors.stop_loss}" id="stop-loss" class="form-input form-input-sm form-input-modal"></price-input>
                                                </div>
                                                <div class="w-full md:w-1/2">
                                                    <label class="form-label-class mt-2 md:mt-1" for="initial-target">Initial Target Price</label>
                                                </div>
                                                <div class="w-full md:w-1/2">
                                                    <price-input :symbol="form.symbol" v-model="form.target_price" :base-code="symbolQuotes.baseCode" :default-value="form.target_price" :class="{'is-invalid': errors.target_price}" id="initial-target" class="form-input form-input-sm form-input-modal"></price-input>
                                                </div>
                                                <div class="w-full md:w-1/2">
                                                    <label class="form-label-class mt-2 md:mt-1" for="reward-risk">Reward / Risk Ratio</label>
                                                </div>
                                                <div class="w-full md:w-1/2">
                                                    <input id="reward-risk" class="form-input form-input-sm" type="text" v-model="riskReward" disabled>
                                                </div>
                                                </template>
                                            </div>
                                        </div>

                                        <div class="w-full md:w-1/2 px-3 lg:px-6">
                                            <div class="flex flex-wrap">

                                                <template v-if="section != 'future-options'">
                                                <div class="w-full md:w-1/2">
                                                    <label class="form-label-class mt-2 md:mt-1" for="potential-profit">Potential Profit</label>
                                                </div>
                                                <div class="w-full md:w-1/2">
                                                    <input id="potential-profit" class="form-input form-input-sm" :class="{'form-input-up' : form.potential_profit > 0, 'form-input-down' : form.potential_profit < 0}" type="number" v-model="potentialProfit" disabled>
                                                </div>
                                                <div class="w-full md:w-1/2">
                                                    <label class="form-label-class mt-2 md:mt-1" for="potential-loss">Potential Loss</label>
                                                </div>
                                                <div class="w-full md:w-1/2">
                                                    <input id="potential-loss" class="form-input form-input-sm" :class="{'form-input-up' : form.potential_loss > 0, 'form-input-down' : form.potential_loss < 0}" type="number" v-model="potentialLoss" disabled>
                                                </div>
                                                </template>

                                                <div class="w-full md:w-1/2">
                                                    <label class="form-label-class mt-2 md:mt-1" for="exit-price">
                                                        <template v-if="section == 'future-options'">Exit Premium</template>
                                                        <template v-else>Exit Price</template>
                                                    </label>
                                                </div>
                                                <div class="w-full md:w-1/2">
                                                    <price-input :symbol="form.symbol" v-model="form.exit_price" :base-code="symbolQuotes.baseCode" :default-value="form.exit_price" :class="{'is-invalid': errors.exit_price}" id="exit-price" class="form-input form-input-sm form-input-modal"></price-input>
                                                </div>
                                                <div class="w-full md:w-1/2">
                                                    <label class="form-label-class mt-2 md:mt-1" for="date-exited">Date Exited</label>
                                                </div>
                                                <div class="w-full md:w-1/2">
                                                    <input class="form-input form-input-sm" :class="{ 'is-invalid' : errors.exit_date }" id="date-exited" type="date" pattern="[0-9]{2}/[0-9]{2}/[0-9]{4}" v-model="form.exit_date">
                                                </div>
                                                <div class="w-full md:w-1/2">
                                                    <label class="form-label-class mt-2 md:mt-1" for="total-commission">Total Commission</label>
                                                </div>
                                                <div class="w-full md:w-1/2">
                                                    <input class="form-input form-input-sm" :class="{ 'is-invalid' : errors.commission }" id="total-commission" type="number" v-model.number="form.commission" min="0">
                                                </div>
                                                <div class="w-full md:w-1/2">
                                                    <label class="form-label-class mt-2 md:mt-1" for="profit-loss">Profit / Loss</label>
                                                </div>
                                                <div class="w-full md:w-1/2">
                                                    <input id="profit-loss" class="form-input form-input-sm" :class="{'form-input-up' : form.profit_loss > 0, 'form-input-down' : form.profit_loss < 0}" type="text" v-model="profitLoss" disabled>
                                                </div>

                                                <!-- Only show for FUTURES order ticket -->
                                                <template v-if="section == 'future'">
                                                    <div class="w-full md:w-1/2">
                                                        <label class="form-label-class mt-2 md:mt-1" for="first-notice">
                                                            <template v-if="form.long_short == 'L'">First Notice Day</template>
                                                            <template v-else>Last Trade Date</template>
                                                        </label>
                                                    </div>
                                                    <div class="w-full md:w-1/2">
                                                        <input class="form-input form-input-sm" :class="{ 'is-invalid' : errors.expire_date }" id="first-notice" type="date" pattern="[0-9]{2}/[0-9]{2}/[0-9]{4}" v-model="form.expire_date">
                                                    </div>
                                                </template>
                                                <!-- Only show for FUTURES order ticket -->

                                                <!-- Only show for OTHERS order ticket -->
                                                <template v-if="section== 'future-options'">
                                                    <div class="w-full md:w-1/2">
                                                        <label class="form-label-class mt-2 md:mt-1" for="expire_date">Expiration Date</label>
                                                    </div>
                                                    <div class="w-full md:w-1/2">
                                                        <input class="form-input form-input-sm" :class="{ 'is-invalid' : errors.expire_date }" id="expire_date" type="date" pattern="[0-9]{2}/[0-9]{2}/[0-9]{4}" v-model="form.expire_date">
                                                    </div>
                                                </template>
                                                <!-- Only show for FUTURES order ticket -->

                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="subsection pb-1"><p class="text-lg font-bold">Additional Information</p></div>
                                <div class="w-full mb-6">
                                    <div class="flex flex-wrap mt-4">
                                        <div class="w-full">
                                            <label class="form-label-class" for="strategy">What Strategy Are You Using?</label>
                                            <textarea class="form-input form-input-sm" :class="{ 'is-invalid' : errors.strategy }" id="strategy" type="text" rows="3" placeholder="" v-model="form.strategy"></textarea>
                                        </div>
                                        <div class="w-full mt-2">
                                            <label class="form-label-class" for="profit-targets">First and Second Profit Targets</label>
                                            <textarea class="form-input form-input-sm" :class="{ 'is-invalid' : errors.targets }" id="profit-targets" type="text" rows="3" placeholder="" v-model="form.targets"></textarea>
                                        </div>
                                        <div class="w-full mt-2">
                                            <label class="form-label-class" for="notes">Notes</label>
                                            <textarea class="form-input form-input-sm" :class="{ 'is-invalid' : errors.notes }" id="notes" type="text" rows="3" placeholder="" v-model="form.notes"></textarea>
                                        </div>
                                    </div>
                                </div>
                                <template v-if="!showModal">
                                    <div class="alert alert-danger mt-4 mb-6 text-sm" v-if="hasErrors()">
                                        <p v-for="error in errors" :key="error">&bull; {{ error[0] }}</p>
                                    </div>
                                    <div class="flex justify-center mt-4 mb-8 text-center">
                                        <div class="btn btn-yellow-ghost text-base inline-flex mx-1 xs:ml-0" @click="onClose">Cancel</div>
                                        <template v-if="isAdd">
                                            <div class="btn btn-yellow text-base inline-flex mx-1 xs:ml-0" @click="onAdd">Add Ticket</div>
                                        </template>
                                        <template v-else>
                                            <div class="btn btn-yellow text-base inline-flex mx-1 xs:ml-0" @click="onUpdate">Update Ticket</div>
                                        </template>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </template>
                    <template v-if="errorSymbolQuotes">
                        <alert-box class="mb-6">
                            <template #body>
                                Error retrieving information.<br/>This symbol has expired or is invalid.
                            </template>
                        </alert-box>
                    </template>
                </div>
                <spinner v-else></spinner>
            </div>
        </teleport>

        <flash-message ref="orderTicketModalFlashRef" :seconds-to-display="6">
            <template #body>
                <div class="text-center">
                    <div>Success! {{ form.symbol }} has been added to Order Tickets.</div>
                    <div><a class="text-xs" :href="getLink()">Go to the Order Tickets</a></div>
                </div>
            </template>
        </flash-message>
    </div>
</template>

<script setup>
import { ref, computed, watch, reactive, onMounted, onBeforeUnmount, nextTick } from 'vue';

const props = defineProps({
    initialValues: {
        type: [Object, Array],
        default() {
            return {};
        },
        required: true
    },
    isModal: Boolean,
    orderTransactionData: Object,
});

const emit = defineEmits(['close']);

const renderTeleportedContent = ref(false);

const orderTicketModalFlashRef = ref(null);
const flash = ref(null);

const sectionMap = { fut: 'future', opt: 'future-options', forex: 'forex' };
const form = reactive({
    id: props.initialValues.id || null,
    symbol: props.initialValues.symbol || null,
    market_name: props.initialValues.market_name || null,
    ticket_number: props.initialValues.ticket_number || '',
    order_type: props.initialValues.order_type || '',
    trend_seeker: props.initialValues.trend_seeker || '',
    trend_signal: props.initialValues.trend_signal || '',
    long_short: props.initialValues.long_short || 'L',
    entry_price: props.initialValues.entry_price || null,
    entry_date: toDateString(props.initialValues.entry_date) || toDateString(new Date()),
    stop_loss: props.initialValues.stop_loss || null,
    target_price: props.initialValues.target_price || null,
    risk_reward: props.initialValues.risk_reward || '-- : --',
    quantity: props.initialValues.quantity || null,
    potential_profit: props.initialValues.potential_profit || null,
    potential_loss: props.initialValues.potential_loss || null,
    exit_price: props.initialValues.exit_price || null,
    exit_date: toDateString(props.initialValues.exit_date),
    commission: props.initialValues.commission || null,
    profit_loss: props.initialValues.profit_loss || null,
    expire_date: toDateString(props.initialValues.expire_date),
    strategy: props.initialValues.strategy || null,
    targets: props.initialValues.targets || null,
    notes: props.initialValues.notes || null,
});

const showModal = ref(props.isModal || false);
const hasChanged = ref(null);
const isAdd = computed(() => !props.initialValues.symbol);
const section = ref(sectionMap[props.initialValues.futopt] || 'future');
// const iniatialForm = ref(JSON.parse(JSON.stringify(formData)));

const sectionQuotes = ref([]);
const symbolQuotes = ref({});

const loading = ref({
    section: ref(props.initialValues.symbol ? false : true),
    symbolQuotes: ref(props.initialValues.symbol ? true : false),
});

const errors = ref({});
const errorSymbolQuotes = ref(false);

const riskReward = computed(() => {
    const entprice = form.entry_price;
    const stoploss = form.stop_loss;
    const tarprice = form.target_price;
    let risk = '--';
    let reward = '--';

    if (entprice && stoploss) {
        risk = entprice - stoploss;
        if (isNaN(risk)) {
            risk = '--';
        } else if (!risk) {
            risk = 1;
        }
    }
    if (entprice && tarprice) {
        reward = tarprice - entprice;
        if (isNaN(reward)) {
            reward = '--';
        }
    }
    if (risk != '--' && reward != '--') {
        reward = Math.round((reward / risk * 10000)) / 10000;
        risk = 1;
        form.risk_reward = reward + ' : ' + risk;
    } else {
        form.risk_reward = '-- : --';
    }

    return form.risk_reward;
});

const potentialProfit = computed(() => {
    potentialProfitLoss();
    return form.potential_profit;
});

const potentialLoss = computed(() => {
    potentialProfitLoss();
    return form.potential_loss;
});

const profitLoss = computed(() => {
    form.profit_loss = '';
    if (symbolQuotes.value.raw) {
        const commission = form.commission || 0;
        if (isNumber(form.entry_price) && isNumber(form.quantity) && isNumber(form.exit_price)) {
            let pointValue = symbolQuotes.value.raw.pointValue || 1;
            if (symbolQuotes.value.raw.symbolType == 10) {
                pointValue = Math.pow(10, symbolQuotes.value.raw.unitCode);
            }
            if (form.long_short == 'L') {
                form.profit_loss = ((form.exit_price - form.entry_price) * form.quantity * pointValue - commission).toFixed(2);
            } else {
                form.profit_loss = ((form.entry_price - form.exit_price) * form.quantity * pointValue - commission).toFixed(2);
            }
        }
    }

    return form.profit_loss;
});

function getLink() {
    return window.route('my-folder.order-tickets.index');
}

function hasErrors() {
    return !(JSON.stringify(errors.value) == '{}' || JSON.stringify(errors.value) == '[]');
}

function generateTicketNumber() {
    const dt = new Date();
    const seed = dt.getYear() + dt.getDay() + dt.getMonth() + dt.getHours() + dt.getMinutes() + dt.getSeconds();
    const holdrand = ((seed * 214013 + 2531011) >> 16) & 0x7fff;
    const holdrand2 = ((holdrand * 214013 + 2531011) >> 16) & 0x7fff;
    form.ticket_number = form.symbol + '-' + holdrand + holdrand2;
}

function potentialProfitLoss() {
    if (symbolQuotes.value.raw) {
        if (isNumber(form.entry_price) && isNumber(form.stop_loss) && isNumber(form.target_price) && isNumber(form.quantity)) {
            let pointValue = symbolQuotes.value.raw.pointValue || 1;
            if (symbolQuotes.value.raw.symbolType == 10) {
                pointValue = Math.pow(10, symbolQuotes.value.raw.unitCode);
            }
            if (form.long_short == 'L') {
                form.potential_profit = ((form.target_price - form.entry_price) * form.quantity * pointValue).toFixed(2);
                form.potential_loss = ((form.stop_loss - form.entry_price) * form.quantity * pointValue).toFixed(2);
            } else {
                form.potential_profit = ((form.entry_price - form.target_price) * form.quantity * pointValue).toFixed(2);
                form.potential_loss = ((form.entry_price - form.stop_loss) * form.quantity * pointValue).toFixed(2);
            }
        } else {
            form.potential_profit = null;
            form.potential_loss = null;
        }
    }
}

function isNumber(n) {
    return !isNaN(parseFloat(n)) && !isNaN(n - 0);
}

function toDateString(value) {
    if (!value) {
        return '';
    }
    const date = new Date(value).toISOString();
    return date.split('T')[0];
}

function onClose() {
    renderTeleportedContent.value = false;
    removeUnloadEvent();
    if (showModal.value) {
        showModal.value = false;
        emit('close');
    } else {
        window.location = window.route('my-folder.order-tickets.index');
    }
}

function onAdd() {
    // create a new entry
    removeUnloadEvent();

    const route = window.route('my-folder.order-tickets.store');
    errors.value = {};
    axios.post(route, form)
        .then((response) => {
            if (showModal.value) {
                orderTicketModalFlashRef.value.display();
                showModal.value = false;
                setTimeout(() => {
                    emit('close');
                }, 6000);
            } else {
                window.location = window.route('my-folder.order-tickets.index');
            }
        }).catch(error => {
            flash.value.error('Error creating «Order Ticket» entry!');
            errors.value = error.response.data.errors;
        });
}

function onUpdate() {
    // update entry
    removeUnloadEvent();

    const route = window.route('my-folder.order-tickets.update');
    errors.value = {};
    axios.put(route, form)
        .then((response) => {
            window.location = window.route('my-folder.order-tickets.index');
        }).catch(error => {
            flash.value.error('Error saving «Order Ticket» entry');
            errors.value = error.response.data.errors;
        });
}

function handleLeave(e) {
    if (hasChanged.value > 1) {
        e.preventDefault();
        e.returnValue = 'Are you sure?';
        return 'Are you sure?';
    }
}

function removeUnloadEvent() {
    window.removeEventListener('beforeunload', handleLeave);
}

function setExpireDate() {
    if (section.value == 'future-options') {
        form.expire_date = (symbolQuotes.value.optionsContractExpiration && symbolQuotes.value.optionsContractExpiration != 'N/A') ? toDateString(symbolQuotes.value.optionsContractExpiration) : '';
    } else {
        if (form.long_short == 'L') {
            form.expire_date = (symbolQuotes.value.contractFirstNoticeDate && symbolQuotes.value.contractFirstNoticeDate != 'N/A') ? toDateString(symbolQuotes.value.contractFirstNoticeDate) : '';
        } else {
            form.expire_date = (symbolQuotes.value.contractExpirationDate && symbolQuotes.value.contractExpirationDate != 'N/A') ? toDateString(symbolQuotes.value.contractExpirationDate) : '';
        }
    }
}

function getSymbolSelectQuotes() {
    const route = window.route('api.quotes.symbol-select');

    loading.value.section = true;

    axios.post(route, { section: section.value })
        .then((response) => {
            sectionQuotes.value = response.data;
        }).catch(error => {
            console.error(error);
        }).then(() => {
            loading.value.section = false;
        });
}

function getSymbolQuotes() {
    const route = window.route('api.quotes');

    loading.value.symbolQuotes = true;

    const params = {
        symbol: form.symbol,
        fields: 'baseCode,symbolType,symbolName,contractFullName,contractMonthYear,lastPrice,tradeTime,contractFirstNoticeDate,contractExpirationDate,pointValue,unitCode,optionsContractExpiration',
        raw: true,
        after: 'getChartTitle'
    };

    axios.post(route, params)
        .then((response) => {
            symbolQuotes.value = response.data.data[0];

            if ((isAdd.value || showModal.value) && symbolQuotes.value) {
                if (symbolQuotes.value.symbolType == 10) {
                    section.value = 'forex';
                    form.section = 'forex';
                } else if (symbolQuotes.value.symbolType == 12) {
                    section.value = 'future-options';
                    form.section = 'opt';
                } else {
                    section.value = 'future';
                    form.section = 'fut';
                }
                form.market_name = symbolQuotes.value.contractFullName || '';
                form.entry_price = symbolQuotes.value.raw.lastPrice || null;
                setExpireDate();

                // populate the data from 'Trade Tracker' page
                if (props.orderTransactionData) {
                    form.long_short = props.orderTransactionData.action === -1 ? 'S' : 'L';
                    form.notes = props.orderTransactionData.notes;
                    form.commission = props.orderTransactionData.commission_total;
                    ['entry_price', 'entry_date', 'quantity', 'exit_price', 'exit_date'].forEach((key) => {
                        if (key == 'entry_date' || key == 'exit_date') {
                            form[key] = (props.orderTransactionData[key]) ? new Date(props.orderTransactionData[key]).toISOString().slice(0, 10) : null;
                        } else {
                            form[key] = (typeof (props.orderTransactionData[key]) == 'string') ? Number(props.orderTransactionData[key]) : props.orderTransactionData[key];
                        }
                    });
                }
            } else {
                errorSymbolQuotes.value = (!symbolQuotes.value);
            }
        }).catch(error => {
            console.error(error);
        }).then(() => {
            loading.value.symbolQuotes = false;
        });
}

watch(() => form.symbol, (newVal, oldVal) => {
    if (newVal) {
        getSymbolQuotes();
    }
});

watch(() => form.long_short, () => {
    setExpireDate();
});

watch(form, () => {
    hasChanged.value++;
}, { deep: true });

watch(() => section, () => {
    if (isAdd.value) {
        form.symbol = '';
    }
    getSymbolSelectQuotes();
});

onMounted(() => {
    window.addEventListener('beforeunload', handleLeave);

    nextTick(() => {
        renderTeleportedContent.value = true;

        if (form.symbol) {
            getSymbolQuotes();
        } else {
            getSymbolSelectQuotes();
        }

        hasChanged.value = null;
    });
});

onBeforeUnmount(() => {
    removeUnloadEvent();
});
</script>


<style lang="scss">
#orderTicketsModal {
    @apply text-left;
    .modal-container {
        width: 60rem;
        @screen xs {
            width: 100%;
        }
    }
}

#orderTicketsForm {
    sup {
        @apply text-gray-500 ml-px pl-px;
    }
    .error {
        @apply mb-1 -mt-1;
    }
    select.form-control-no-shadow {
        @apply mb-1;
    }
}
</style>
